import { useState, useEffect, useRef, useCallback } from "react";
import { apiService } from "@/services";

export type EmailDomainStatus =
  | "idle"
  | "invalid-format"
  | "verifying"
  | "valid"
  | "disposable"
  | "personal"
  | "error";

const EMAIL_REGEX = /^[a-zA-Z0-9@.\-_]+$/;
const VALID_EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const DEBOUNCE_MS = 1000;

interface ValidateDomainResponse {
  valid: boolean;
  reason?: string;
}

export function useEmailDomainValidation() {
  const [status, setStatus] = useState<EmailDomainStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastValidatedDomainRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cleanup = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const sanitizeEmail = useCallback((value: string): string => {
    return value
      .split("")
      .filter((ch) => EMAIL_REGEX.test(ch))
      .join("");
  }, []);

  const validateEmail = useCallback(
    (email: string) => {
      cleanup();
      setErrorMessage(null);

      if (!email || email.trim() === "") {
        setStatus("idle");
        lastValidatedDomainRef.current = null;
        return;
      }

      if (!VALID_EMAIL_REGEX.test(email)) {
        setStatus("invalid-format");
        lastValidatedDomainRef.current = null;
        return;
      }

      const domain = email.split("@")[1]?.toLowerCase();
      if (!domain) {
        setStatus("invalid-format");
        return;
      }

      if (lastValidatedDomainRef.current === domain) {
        return;
      }

      setStatus("verifying");

      debounceTimerRef.current = setTimeout(async () => {
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
          const response = await apiService.request<ValidateDomainResponse>(
            "/auth/email/validateDomain",
            {
              method: "POST",
              body: JSON.stringify({ domain }),
              requireAuth: false,
              signal: controller.signal,
            },
          );

          if (controller.signal.aborted) return;

          lastValidatedDomainRef.current = domain;

          if (response.valid) {
            setStatus("valid");
            setErrorMessage(null);
          } else if (response.reason === "disposable") {
            setStatus("disposable");
            setErrorMessage("Disposable email addresses are not accepted");
          } else if (response.reason === "personal") {
            setStatus("personal");
            setErrorMessage("Please use a business email address");
          } else {
            setStatus("error");
            setErrorMessage("This email domain is not accepted");
          }
        } catch (err: unknown) {
          if (controller.signal.aborted) return;
          setStatus("error");
          setErrorMessage("Unable to verify email domain");
        }
      }, DEBOUNCE_MS);
    },
    [cleanup],
  );

  const reset = useCallback(() => {
    cleanup();
    setStatus("idle");
    setErrorMessage(null);
    lastValidatedDomainRef.current = null;
  }, [cleanup]);

  const isBlocking =
    status === "verifying" ||
    status === "idle" ||
    status === "invalid-format" ||
    status === "disposable" ||
    status === "personal" ||
    status === "error";

  return {
    status,
    errorMessage,
    isBlocking,
    validateEmail,
    sanitizeEmail,
    reset,
  };
}
